import React, { useState, useEffect } from 'react';
import { 
  Accessibility, 
  Eye, 
  Type, 
  ZoomIn, 
  ZoomOut,
  Contrast,
  MousePointer,
  Keyboard,
  Volume2,
  X,
  Check,
  Palette,
  MinusCircle,
  Sparkles
} from 'lucide-react';
import { Button } from './ui/button';

const STORAGE_KEY = 'eyvo_accessibility_prefs';

export default function AccessibilityWidget() {
  // Hide when rendered inside an iframe (e.g., guide slide-over panels)
  const isIframe = typeof window !== 'undefined' && window.self !== window.top;
  const [isOpen, setIsOpen] = useState(false);
  const [prefs, setPrefs] = useState({
    textSize: 100,
    highContrast: false,
    grayscale: false,
    invertColors: false,
    highlightLinks: false,
    highlightHeadings: false,
    largerCursor: false,
    readingGuide: false,
    reduceMotion: false,
    dyslexiaFont: false,
    textSpacing: false,
  });

  // Load preferences from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        setPrefs(JSON.parse(saved));
      }
    } catch (e) {
      console.error('Failed to load accessibility preferences:', e);
    }
  }, []);

  // Apply preferences
  useEffect(() => {
    const root = document.documentElement;
    
    // Text size
    root.style.fontSize = `${prefs.textSize}%`;
    
    // High contrast
    if (prefs.highContrast) {
      root.classList.add('a11y-high-contrast');
    } else {
      root.classList.remove('a11y-high-contrast');
    }
    
    // Grayscale
    if (prefs.grayscale) {
      root.style.filter = 'grayscale(100%)';
    } else if (prefs.invertColors) {
      root.style.filter = 'invert(1) hue-rotate(180deg)';
    } else {
      root.style.filter = 'none';
    }
    
    // Highlight links
    if (prefs.highlightLinks) {
      root.classList.add('a11y-highlight-links');
    } else {
      root.classList.remove('a11y-highlight-links');
    }
    
    // Highlight headings
    if (prefs.highlightHeadings) {
      root.classList.add('a11y-highlight-headings');
    } else {
      root.classList.remove('a11y-highlight-headings');
    }
    
    // Larger cursor
    if (prefs.largerCursor) {
      root.classList.add('a11y-large-cursor');
    } else {
      root.classList.remove('a11y-large-cursor');
    }
    
    // Reading guide
    if (prefs.readingGuide) {
      root.classList.add('a11y-reading-guide');
      enableReadingGuide();
    } else {
      root.classList.remove('a11y-reading-guide');
      disableReadingGuide();
    }
    
    // Reduce motion
    if (prefs.reduceMotion) {
      root.style.setProperty('--transition-speed', '0ms');
    } else {
      root.style.removeProperty('--transition-speed');
    }
    
    // Dyslexia font
    if (prefs.dyslexiaFont) {
      root.classList.add('a11y-dyslexia-font');
    } else {
      root.classList.remove('a11y-dyslexia-font');
    }
    
    // Text spacing
    if (prefs.textSpacing) {
      root.classList.add('a11y-text-spacing');
    } else {
      root.classList.remove('a11y-text-spacing');
    }
    
    // Save to localStorage
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch (e) {
      console.error('Failed to save accessibility preferences:', e);
    }
  }, [prefs]);

  const updatePref = (key, value) => {
    setPrefs(prev => ({ ...prev, [key]: value }));
  };

  const resetAll = () => {
    setPrefs({
      textSize: 100,
      highContrast: false,
      grayscale: false,
      invertColors: false,
      highlightLinks: false,
      highlightHeadings: false,
      largerCursor: false,
      readingGuide: false,
      reduceMotion: false,
      dyslexiaFont: false,
      textSpacing: false,
    });
  };

  // Reading guide (line highlight)
  const enableReadingGuide = () => {
    const guide = document.createElement('div');
    guide.id = 'reading-guide';
    guide.style.cssText = `
      position: fixed;
      left: 0;
      right: 0;
      height: 3px;
      background: rgba(59, 130, 246, 0.6);
      pointer-events: none;
      z-index: 9998;
      transition: top 0.1s ease;
    `;
    document.body.appendChild(guide);
    
    const moveGuide = (e) => {
      guide.style.top = e.clientY + 'px';
    };
    
    document.addEventListener('mousemove', moveGuide);
    guide.dataset.listener = 'mousemove';
  };

  const disableReadingGuide = () => {
    const guide = document.getElementById('reading-guide');
    if (guide) {
      guide.remove();
    }
  };

  // Keyboard shortcut (Alt+A)
  useEffect(() => {
    const handler = (e) => {
      if (e.altKey && e.key === 'a') {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  if (isIframe) return null;

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-[9999] w-14 h-14 rounded-full bg-primary text-white shadow-lg hover:shadow-xl transition-all hover:scale-110 flex items-center justify-center"
        aria-label="Open Accessibility Menu (Alt+A)"
        data-testid="accessibility-widget-button"
      >
        {/* Vitruvian Man accessibility icon - arms raised up with gap from head */}
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round"
          className="h-10 w-10"
        >
          {/* Head */}
          <circle cx="12" cy="4" r="2.5" />
          {/* Body */}
          <line x1="12" y1="6.5" x2="12" y2="15" />
          {/* Arms — angled down slightly, clear gap from head */}
          <line x1="12" y1="9.5" x2="4" y2="11" />
          <line x1="12" y1="9.5" x2="20" y2="11" />
          {/* Legs spread */}
          <line x1="12" y1="15" x2="7" y2="22" />
          <line x1="12" y1="15" x2="17" y2="22" />
        </svg>
      </button>

      {/* Accessibility panel */}
      {isOpen && (
        <div
          className="fixed bottom-24 right-6 z-[9999] w-96 max-h-[80vh] overflow-y-auto bg-background border-2 rounded-xl shadow-2xl"
          role="dialog"
          aria-label="Accessibility Options"
          data-testid="accessibility-panel"
        >
          {/* Header */}
          <div className="sticky top-0 bg-background border-b p-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Accessibility className="h-5 w-5 text-primary" />
              <h2 className="font-semibold text-lg">Accessibility</h2>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 hover:bg-muted rounded"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 space-y-6">
            {/* Text Adjustments */}
            <section>
              <h3 className="font-medium text-sm mb-3 flex items-center gap-2">
                <Type className="h-4 w-4" />
                Text Adjustments
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Text Size: {prefs.textSize}%</span>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updatePref('textSize', Math.max(80, prefs.textSize - 10))}
                      aria-label="Decrease text size"
                    >
                      <ZoomOut className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updatePref('textSize', 100)}
                      aria-label="Reset text size"
                    >
                      100%
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updatePref('textSize', Math.min(200, prefs.textSize + 10))}
                      aria-label="Increase text size"
                    >
                      <ZoomIn className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <ToggleOption
                  label="Dyslexia-friendly font"
                  checked={prefs.dyslexiaFont}
                  onChange={(val) => updatePref('dyslexiaFont', val)}
                  description="Easier to read for dyslexic users"
                />

                <ToggleOption
                  label="Increased text spacing"
                  checked={prefs.textSpacing}
                  onChange={(val) => updatePref('textSpacing', val)}
                  description="More space between lines and letters"
                />
              </div>
            </section>

            {/* Visual Adjustments */}
            <section>
              <h3 className="font-medium text-sm mb-3 flex items-center gap-2">
                <Eye className="h-4 w-4" />
                Visual Adjustments
              </h3>
              <div className="space-y-3">
                <ToggleOption
                  label="High contrast"
                  checked={prefs.highContrast}
                  onChange={(val) => updatePref('highContrast', val)}
                  description="Increased contrast for better visibility"
                  icon={<Contrast className="h-4 w-4" />}
                />

                <ToggleOption
                  label="Grayscale"
                  checked={prefs.grayscale}
                  onChange={(val) => {
                    updatePref('grayscale', val);
                    if (val) updatePref('invertColors', false);
                  }}
                  description="Remove all colors"
                />

                <ToggleOption
                  label="Invert colors"
                  checked={prefs.invertColors}
                  onChange={(val) => {
                    updatePref('invertColors', val);
                    if (val) updatePref('grayscale', false);
                  }}
                  description="Reverse light and dark"
                  icon={<Palette className="h-4 w-4" />}
                />
              </div>
            </section>

            {/* Navigation Aids */}
            <section>
              <h3 className="font-medium text-sm mb-3 flex items-center gap-2">
                <MousePointer className="h-4 w-4" />
                Navigation Aids
              </h3>
              <div className="space-y-3">
                <ToggleOption
                  label="Highlight links"
                  checked={prefs.highlightLinks}
                  onChange={(val) => updatePref('highlightLinks', val)}
                  description="Make all links more visible"
                />

                <ToggleOption
                  label="Highlight headings"
                  checked={prefs.highlightHeadings}
                  onChange={(val) => updatePref('highlightHeadings', val)}
                  description="Make section headings stand out"
                />

                <ToggleOption
                  label="Larger cursor"
                  checked={prefs.largerCursor}
                  onChange={(val) => updatePref('largerCursor', val)}
                  description="Bigger mouse pointer"
                  icon={<MousePointer className="h-4 w-4" />}
                />

                <ToggleOption
                  label="Reading guide"
                  checked={prefs.readingGuide}
                  onChange={(val) => updatePref('readingGuide', val)}
                  description="Horizontal line follows your mouse"
                />
              </div>
            </section>

            {/* Motion & Animation */}
            <section>
              <h3 className="font-medium text-sm mb-3 flex items-center gap-2">
                <Sparkles className="h-4 w-4" />
                Motion & Animation
              </h3>
              <div className="space-y-3">
                <ToggleOption
                  label="Reduce motion"
                  checked={prefs.reduceMotion}
                  onChange={(val) => updatePref('reduceMotion', val)}
                  description="Disable animations and transitions"
                  icon={<MinusCircle className="h-4 w-4" />}
                />
              </div>
            </section>

            {/* Screen Reader Info */}
            <section className="border rounded-lg p-3 bg-muted/30">
              <h3 className="font-medium text-sm mb-2 flex items-center gap-2">
                <Volume2 className="h-4 w-4" />
                Screen Reader Support
              </h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                This application is optimized for screen readers with proper ARIA labels, 
                landmarks, and semantic HTML. Press <kbd className="px-1 py-0.5 rounded bg-background border text-[10px]">Tab</kbd> to navigate 
                through interactive elements.
              </p>
            </section>

            {/* Keyboard Shortcuts */}
            <section className="border rounded-lg p-3 bg-muted/30">
              <h3 className="font-medium text-sm mb-2 flex items-center gap-2">
                <Keyboard className="h-4 w-4" />
                Keyboard Shortcuts
              </h3>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li><kbd className="px-1 py-0.5 rounded bg-background border text-[10px]">Alt + A</kbd> - Toggle this menu</li>
                <li><kbd className="px-1 py-0.5 rounded bg-background border text-[10px]">Tab</kbd> - Navigate forward</li>
                <li><kbd className="px-1 py-0.5 rounded bg-background border text-[10px]">Shift + Tab</kbd> - Navigate backward</li>
                <li><kbd className="px-1 py-0.5 rounded bg-background border text-[10px]">Enter</kbd> - Activate element</li>
                <li><kbd className="px-1 py-0.5 rounded bg-background border text-[10px]">Esc</kbd> - Close dialogs</li>
              </ul>
            </section>

            {/* Reset button */}
            <Button
              variant="outline"
              className="w-full"
              onClick={resetAll}
              data-testid="accessibility-reset-button"
            >
              Reset All Settings
            </Button>
          </div>
        </div>
      )}

      {/* Global CSS injection */}
      <style>{`
        /* High contrast mode */
        .a11y-high-contrast {
          --background: 0 0% 100%;
          --foreground: 0 0% 0%;
          --primary: 210 100% 40%;
          --muted: 0 0% 90%;
          --border: 0 0% 20%;
        }
        
        .a11y-high-contrast.dark {
          --background: 0 0% 0%;
          --foreground: 0 0% 100%;
          --primary: 210 100% 60%;
          --muted: 0 0% 15%;
          --border: 0 0% 80%;
        }

        /* Highlight links */
        .a11y-highlight-links a {
          text-decoration: underline !important;
          text-decoration-thickness: 2px !important;
          text-underline-offset: 2px !important;
          font-weight: 600 !important;
        }

        /* Highlight headings */
        .a11y-highlight-headings h1,
        .a11y-highlight-headings h2,
        .a11y-highlight-headings h3,
        .a11y-highlight-headings h4,
        .a11y-highlight-headings h5,
        .a11y-highlight-headings h6 {
          background: rgba(59, 130, 246, 0.15) !important;
          padding: 0.25rem 0.5rem !important;
          border-left: 4px solid rgb(59, 130, 246) !important;
        }

        /* Large cursor */
        .a11y-large-cursor,
        .a11y-large-cursor * {
          cursor: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><path fill="black" stroke="white" stroke-width="2" d="M3 3 L13 28 L16 18 L28 15 Z"/></svg>') 0 0, auto !important;
        }

        /* Dyslexia font */
        .a11y-dyslexia-font * {
          font-family: 'Comic Sans MS', 'OpenDyslexic', sans-serif !important;
        }

        /* Text spacing */
        .a11y-text-spacing {
          line-height: 1.8 !important;
          letter-spacing: 0.12em !important;
          word-spacing: 0.16em !important;
        }
        
        .a11y-text-spacing p {
          margin-bottom: 2em !important;
        }

        /* Focus visible styles */
        *:focus-visible {
          outline: 3px solid rgb(59, 130, 246) !important;
          outline-offset: 2px !important;
        }

        /* Skip to main content link */
        .skip-to-main {
          position: absolute;
          left: -9999px;
          z-index: 999;
          padding: 1em;
          background-color: rgb(59, 130, 246);
          color: white;
          text-decoration: none;
        }
        
        .skip-to-main:focus {
          left: 50%;
          transform: translateX(-50%);
          top: 0;
        }
      `}</style>
    </>
  );
}

// Toggle option component
function ToggleOption({ label, checked, onChange, description, icon }) {
  return (
    <div className="flex items-start gap-3">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-4 w-4 rounded border-input cursor-pointer"
        id={`a11y-${label.replace(/\s+/g, '-')}`}
      />
      <div className="flex-1">
        <label
          htmlFor={`a11y-${label.replace(/\s+/g, '-')}`}
          className="text-sm font-medium cursor-pointer flex items-center gap-2"
        >
          {icon}
          {label}
          {checked && <Check className="h-3 w-3 text-green-600" />}
        </label>
        {description && (
          <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
        )}
      </div>
    </div>
  );
}
